import { useCallback, useEffect, useRef, useState } from 'react';
import { onConnectionChange, onEvent, subscribe } from '../lib/realtime';
import type { ConnectionState, RealtimeEvent, Topic } from '../lib/realtime';
import { api, ApiError } from '../lib/api';

/**
 * Subscribes to realtime topics and calls `handler` for every matching event.
 *
 * `onResync` fires when the socket comes back after a drop. Panels use it to
 * refetch, because a reconnect means an unknown number of events were missed
 * and a queue that silently lags reality is worse than one that flickers.
 */
export function useRealtime(
  topics: Topic[],
  handler: (event: RealtimeEvent) => void,
  onResync?: () => void,
): ConnectionState {
  const [state, setState] = useState<ConnectionState>('connecting');
  const handlerRef = useRef(handler);
  const resyncRef = useRef(onResync);
  handlerRef.current = handler;
  resyncRef.current = onResync;

  const key = topics.join(',');

  useEffect(() => {
    const unsubscribe = subscribe(key.split(',') as Topic[]);
    const offEvent = onEvent((event) => {
      if (!key.split(',').includes(event.topic)) return;
      handlerRef.current(event);
    });
    const offState = onConnectionChange((next, reconnected) => {
      setState(next);
      if (reconnected) resyncRef.current?.();
    });
    return () => {
      unsubscribe();
      offEvent();
      offState();
    };
  }, [key]);

  return state;
}

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  set: (updater: (current: T | null) => T | null) => void;
}

/** GET a path, with an imperative reload for realtime-triggered refetches. */
export function useResource<T>(path: string | null, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    api
      .get<T>(path, controller.signal)
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not load data');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((updater: (current: T | null) => T | null) => setData(updater), []);

  return { data, error, loading, reload, set };
}

/** A ticking clock, so elapsed counters advance without a re-fetch. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Debounces a value — used for the incident search box. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Transient banner state for success/error feedback on an action. */
export function useFlash(): [
  { kind: 'ok' | 'error'; message: string } | null,
  (kind: 'ok' | 'error', message: string) => void,
] {
  const [flash, setFlash] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);
  const show = useCallback((kind: 'ok' | 'error', message: string) => {
    setFlash({ kind, message });
    window.setTimeout(() => setFlash(null), kind === 'ok' ? 3000 : 6000);
  }, []);
  return [flash, show];
}
